import React from 'react';
import { motion } from 'framer-motion';
import { Star, Info, Compass, MapPin, Calendar, Clock, Maximize2, Share2 } from 'lucide-react';
import Galaxy from '../components/cosmic/Galaxy';

const CosmicMap: React.FC = () => {
  const [details, setDetails] = React.useState({
    date: '2026-03-23',
    time: '22:41',
    place: 'Mumbai, India'
  });

  const planets = [
    { name: 'Sun', sign: 'Aries', degree: '12°', icon: '☀️', color: '#FFD700' },
    { name: 'Moon', sign: 'Cancer', degree: '24°', icon: '🌙', color: '#F0F8FF' },
    { name: 'Mars', sign: 'Leo', degree: '05°', icon: '🔴', color: '#FF4500' },
    { name: 'Venus', sign: 'Taurus', degree: '18°', icon: '✨', color: '#FF69B4' },
    { name: 'Jupiter', sign: 'Pisces', degree: '29°', icon: '🪐', color: '#DAA520' },
    { name: 'Saturn', sign: 'Aquarius', degree: '14°', icon: '🌑', color: '#708090' }
  ];

  return (
    <div className="h-[calc(100vh-80px)] bg-midnight relative overflow-hidden flex flex-col noise-overlay">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <Galaxy 
          density={2.5} 
          speed={0.15} 
          hueShift={240} 
          saturation={0.6}
          glowIntensity={0.7}
          mouseRepulsion={false}
          autoCenterRepulsion={0.4}
        />
      </div>
      <div className="absolute inset-0 bg-midnight/30 z-1" />

      {/* Content */}
      <div className="relative z-10 flex-grow flex flex-col lg:flex-row">
        {/* Left: Controls */}
        <div className="w-full lg:w-[400px] p-10 bg-midnight/40 backdrop-blur-3xl border-r border-white/5 flex flex-col shadow-2xl">
          <div className="mb-12">
            <span className="text-gold text-[10px] font-bold uppercase tracking-[0.3em] mb-4 block">Interactive Tool</span>
            <h1 className="text-4xl font-serif font-bold text-white mb-4">Cosmic Map</h1>
            <p className="text-gray-500 text-sm font-light leading-relaxed">Visualize the precise planetary alignments at any given moment in time and space.</p>
          </div>

          <div className="space-y-8 flex-grow">
            <div className="space-y-3">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">Birth Date</label>
              <div className="relative">
                <Calendar className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-gold/50" />
                <input 
                  type="date" 
                  className="w-full pl-14 pr-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white transition-all"
                  value={details.date}
                  onChange={(e) => setDetails({...details, date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">Birth Time</label>
              <div className="relative">
                <Clock className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-gold/50" />
                <input 
                  type="time" 
                  className="w-full pl-14 pr-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white transition-all"
                  value={details.time}
                  onChange={(e) => setDetails({...details, time: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">Birth Place</label>
              <div className="relative">
                <MapPin className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-gold/50" />
                <input 
                  type="text" 
                  className="w-full pl-14 pr-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white transition-all"
                  value={details.place}
                  onChange={(e) => setDetails({...details, place: e.target.value})}
                />
              </div>
            </div>

            <button className="w-full py-5 bg-gold text-midnight font-bold rounded-2xl hover:bg-white transition-all duration-500 flex items-center justify-center shadow-2xl shadow-gold/10 group">
              <Compass className="w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-1000" />
              Recalculate Map
            </button>
          </div>

          <div className="mt-12 p-6 bg-gold/5 border border-gold/10 rounded-2xl">
            <div className="flex items-start space-x-4">
              <Info className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <p className="text-[10px] text-gray-500 leading-relaxed font-light">
                This interactive map uses real-time astronomical data to project the celestial sphere. For a full interpretation, book a session with our experts.
              </p>
            </div>
          </div>
        </div>

        {/* Center: Visualization */}
        <div className="flex-grow relative flex items-center justify-center p-12">
          <div className="absolute top-10 right-10 flex space-x-4">
            <button className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-gold hover:border-gold transition-all">
              <Maximize2 className="w-4 h-4" />
            </button>
            <button className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-gold hover:border-gold transition-all">
              <Share2 className="w-4 h-4" />
            </button>
          </div>

          <div className="relative w-full max-w-3xl aspect-square">
            {/* Zodiac Ring */}
            <div className="absolute inset-0 border-[1px] border-gold/20 rounded-full animate-[spin_120s_linear_infinite]" />
            <div className="absolute inset-8 border-[1px] border-gold/10 rounded-full animate-[spin_180s_linear_infinite_reverse]" />
            <div className="absolute inset-16 border-[1px] border-gold/5 rounded-full" />
            
            {/* Zodiac Signs Labels */}
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30) * (Math.PI / 180);
              const radius = 48; // percentage
              const x = 50 + radius * Math.cos(angle);
              const y = 50 + radius * Math.sin(angle);
              const signs = ['ARI', 'TAU', 'GEM', 'CAN', 'LEO', 'VIR', 'LIB', 'SCO', 'SAG', 'CAP', 'AQU', 'PIS'];
              
              return (
                <div 
                  key={i} 
                  className="absolute -translate-x-1/2 -translate-y-1/2 text-[8px] font-black text-gold/30 tracking-widest"
                  style={{ left: `${x}%`, top: `${y}%`, transform: `translate(-50%, -50%) rotate(${i * 30 + 90}deg)` }}
                >
                  {signs[i]}
                </div>
              );
            })}
            
            {/* Planets */}
            {planets.map((planet, idx) => {
              const angle = (idx * (360 / planets.length) + 45) * (Math.PI / 180);
              const radius = 35; // percentage
              const x = 50 + radius * Math.cos(angle);
              const y = 50 + radius * Math.sin(angle);

              return (
                <motion.div
                  key={planet.name}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: idx * 0.15, type: "spring" }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 group cursor-pointer z-20"
                  style={{ left: `${x}%`, top: `${y}%` }}
                >
                  <div className="relative">
                    <div 
                      className="w-14 h-14 bg-midnight/60 backdrop-blur-2xl border border-white/10 rounded-full flex items-center justify-center text-2xl group-hover:scale-125 group-hover:border-gold transition-all duration-500 shadow-2xl"
                      style={{ boxShadow: `0 0 20px ${planet.color}20` }}
                    >
                      {planet.icon}
                    </div>
                    
                    {/* Orbit Line */}
                    <div 
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] h-[200%] border border-white/5 rounded-full pointer-events-none"
                      style={{ width: `${radius * 2}%`, height: `${radius * 2}%` }}
                    />

                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 opacity-0 group-hover:opacity-100 transition-all duration-500 whitespace-nowrap pointer-events-none transform translate-y-2 group-hover:translate-y-0">
                      <div className="bg-midnight/90 backdrop-blur-2xl border border-gold/20 p-4 rounded-2xl text-center shadow-2xl">
                        <p className="text-[10px] font-black text-gold uppercase tracking-[0.2em] mb-1">{planet.name}</p>
                        <p className="text-xs text-white font-serif italic">{planet.sign} {planet.degree}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Center Star */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="relative flex items-center justify-center">
                <Star className="w-20 h-20 text-gold animate-pulse-slow" />
                <div className="absolute inset-0 bg-gold/20 blur-[60px] rounded-full" />
                <div className="absolute inset-0 bg-gold/10 blur-[100px] rounded-full animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Right: Legend (Desktop only) */}
        <div className="hidden xl:flex w-[350px] p-10 bg-midnight/40 backdrop-blur-3xl border-l border-white/5 flex-col shadow-2xl">
          <h3 className="text-2xl font-serif font-bold text-white mb-10">Celestial Positions</h3>
          <div className="space-y-4 overflow-y-auto pr-4 custom-scrollbar">
            {planets.map((planet) => (
              <div key={planet.name} className="flex items-center justify-between p-5 glass-card group">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-xl group-hover:bg-gold/10 transition-all">
                    {planet.icon}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white group-hover:text-gold transition-colors">{planet.name}</p>
                    <p className="text-[9px] text-gray-600 uppercase tracking-widest mt-0.5">{planet.sign}</p>
                  </div>
                </div>
                <span className="text-gold font-serif font-bold text-lg">{planet.degree}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/5">
            <div className="flex items-center justify-between text-[10px] text-gray-600 uppercase tracking-widest">
              <span>Ascendant</span>
              <span className="text-white font-bold">Scorpio 08°</span>
            </div>
            <div className="flex items-center justify-between text-[10px] text-gray-600 uppercase tracking-widest mt-4">
              <span>Midheaven</span>
              <span className="text-white font-bold">Leo 15°</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CosmicMap;
